'use client'

import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Typography,
  Link,
  Snackbar,
  IconButton,
} from '@mui/material'
import NextLink from 'next/link'
import { type ReactNode, useState } from 'react'
import { Close } from '@mui/icons-material'
import { isEmailValid, isPersonalEmail } from 'utils'
import { isClerkAPIResponseError, useSignUp, useSignIn } from 'auth/clerk'
import * as Sentry from '@sentry/nextjs'
import { redirect } from 'next/navigation'
import LogoTeach from '@/assets/logo/teach-logo.svg'
import { GoogleButton } from '@/components/GoogleButton'
import { InputField } from '@/components/InputField'
import Loading from '@/app/loading'

export function TrialSignUp({ host }: { host: string }) {
  const [isVerifying, setIsVerifying] = useState(false)
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [workEmail, setWorkEmail] = useState('')
  const { signUp, isLoaded: isSignUpLoaded, setActive } = useSignUp()
  const { signIn } = useSignIn()
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isFormLoading, setIsFormLoading] = useState(false)

  if (!isSignUpLoaded) return <Loading background="white" />

  const onUseAnotherMethod = () => {
    setIsVerifying(false)
    setIsFormLoading(false)
  }

  const signInExistingUser = async (emailAddress: string) => {
    if (!signIn) {
      return
    }

    try {
      const signInAttempt = await signIn.create({
        identifier: emailAddress,
      })

      if (
        signInAttempt.status === 'needs_identifier' ||
        signInAttempt.status === 'needs_first_factor'
      ) {
        // Existing users are funneled into the same email-link verification flow, not a password prompt.
        const emailLinkFactor = signInAttempt.supportedFirstFactors?.find(
          (factor) => factor.strategy === 'email_link'
        )

        if (emailLinkFactor && 'emailAddressId' in emailLinkFactor) {
          await signInAttempt.prepareFirstFactor({
            strategy: 'email_link',
            emailAddressId: emailLinkFactor.emailAddressId,
            redirectUrl: `${host}/trial-sign-up/verify`,
          })
          setIsVerifying(true)
          setWorkEmail(emailAddress)
          return
        }
        return
      }

      if (
        signInAttempt.status === 'complete' &&
        signInAttempt.createdSessionId
      ) {
        await setActive({ session: signInAttempt.createdSessionId })
        redirect('/trial-sign-up/create-trial')
      }
    } catch (signInError) {
      redirect(
        `/sign-in?redirect_url=${encodeURIComponent('/trial-sign-up/create-trial')}`
      )
    }
  }

  const onGoogleClick = async () => {
    if (!hasAcceptedTerms) {
      setErrorMessage(
        'You must accept our Terms of Use and Privacy Policy to continue.'
      )
      return
    }

    try {
      setIsGoogleLoading(true)
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${host}/trial-sign-up/sso-callback`,
        redirectUrlComplete: `${host}/trial-sign-up/create-trial`,
      })
    } catch (exception: unknown) {
      setIsGoogleLoading(false)
      setIsVerifying(false)
      Sentry.captureException(exception)
      setErrorMessage('An error occurred.')
    }
  }

  const onFormButtonClick = async () => {
    if (isPersonalEmail(workEmail)) {
      setErrorMessage(
        'Personal email addresses like Gmail are not allowed. Please use your work email.'
      )
      return
    }
    if (!hasAcceptedTerms) {
      setErrorMessage(
        'You must accept our Terms of Use and Privacy Policy to continue.'
      )
      return
    }
    if (!firstName || !lastName) {
      setErrorMessage('First Name and Last Name are required.')
      return
    }
    if (!isEmailValid(workEmail)) {
      setErrorMessage('Please enter a valid email address.')
      return
    }

    try {
      const { startEmailLinkFlow } = signUp.createEmailLinkFlow()
      setIsFormLoading(true)
      await signUp.create({
        emailAddress: workEmail,
        firstName,
        lastName,
      })
      setIsVerifying(true)

      await startEmailLinkFlow({
        redirectUrl: `${host}/trial-sign-up/verify`,
      })
    } catch (exception: unknown) {
      setIsVerifying(false)
      if (isClerkAPIResponseError(exception)) {
        if (
          exception.errors.some(
            (error) => error.code === 'form_identifier_exists'
          )
        ) {
          await signInExistingUser(workEmail)
          return
        }
      }

      Sentry.captureException(exception)
      setErrorMessage(
        'An unknown error occurred. If the problem persists, please contact support.'
      )
    } finally {
      setIsFormLoading(false)
    }
  }

  return (
    <>
      <Stack spacing={3} sx={{ alignItems: 'center' }}>
        <Typography variant="h1">
          Start your 14-day free trial of PBLWorks TEACH
        </Typography>
        <Paper elevation={12} sx={{ width: 400, p: 4 }}>
          <Stack spacing={2.5}>
            <LogoTeach
              style={{ display: 'block', height: 36, alignSelf: 'flex-start' }}
            />
            {isVerifying ? (
              <Stack spacing={2}>
                <Typography variant="h4">Check your email</Typography>
                <Typography>
                  An email has been sent to <strong>{workEmail}</strong> with
                  instructions to activate your trial. You can now close this
                  tab.
                </Typography>
                <Link onClick={onUseAnotherMethod} href="#">
                  Use another method
                </Link>
              </Stack>
            ) : (
              <>
                <Typography variant="h4" fontWeight={700}>
                  Choose how you want to sign in
                </Typography>
                <GoogleButton
                  onClick={onGoogleClick}
                  disabled={isFormLoading}
                  loading={isGoogleLoading}
                >
                  Use your Google Workspace account
                </GoogleButton>
                <Divider>or</Divider>
                <Grid container spacing={2.5}>
                  <Grid size={6}>
                    <InputField
                      label="First Name"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value)
                      }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <InputField
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value)
                      }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <InputField
                      label="Work Email Address"
                      value={workEmail}
                      onChange={(e) => {
                        setWorkEmail(e.target.value)
                      }}
                    />
                  </Grid>
                </Grid>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={onFormButtonClick}
                  loading={isFormLoading}
                  disabled={isGoogleLoading}
                >
                  Use your work email address
                </Button>
                <Divider />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasAcceptedTerms}
                      onChange={(e) => {
                        setHasAcceptedTerms(e.target.checked)
                      }}
                    />
                  }
                  label={
                    <>
                      I agree to the PBLWorks{' '}
                      <Link
                        href="https://www.pblworks.org/terms#terms-of-use"
                        target="_blank"
                      >
                        Terms of Use
                      </Link>{' '}
                      and{' '}
                      <Link
                        href="https://www.pblworks.org/terms#privacy-policy"
                        target="_blank"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </>
                  }
                />
              </>
            )}
          </Stack>
        </Paper>
        <Typography>
          Already have an active TEACH subscription?{' '}
          <Link component={NextLink} href="/home">
            Sign in here
          </Link>
        </Typography>
      </Stack>
      <Snackbar
        open={Boolean(errorMessage)}
        onClose={() => {
          setErrorMessage(null)
        }}
        message={errorMessage}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              setErrorMessage(null)
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </>
  )
}
